import React from "react";
import { ActivityMetricsSkeleton } from "../../../components/Dashboard/SkeletonLoaders";
import "../../../styles/activity-metrics.css" // We'll create/update this or use inline

const ActivityMetrics = ({ summaryMetrics, loadingSummary }) => {
  // Helpers
  const safeDiv = (num, den) => den ? (num / den) : 0;
  const safePercent = (num, den) => den ? Math.round((num / den) * 100) : 0;
  
  const formatDuration = (ms) => {
    if (ms === 0 || !ms) return "0sec";
    const seconds = ms / 1000;
    if (seconds < 60) return `${Math.round(seconds)}sec`;
    const minutes = seconds / 60;
    if (minutes < 60) return `${Math.round(minutes)}min`;
    const hours = minutes / 60;
    if (hours < 24) return `${Math.round(hours)}hrs`;
    const days = hours / 24;
    return `${days.toFixed(1)}days`;
  };

  // Values
  const profiles = summaryMetrics.funnelProfiles || 0;
  const activated = summaryMetrics.funnelActivated || 0;
  const timeValInMilliseconds = summaryMetrics.funnelAvgTimeMilliseconds !== undefined ? summaryMetrics.funnelAvgTimeMilliseconds : 0;
  const formattedTime = formatDuration(timeValInMilliseconds);
  const activationRate = summaryMetrics.funnelActivationRate || 0;
  
  const wau = summaryMetrics.avgWAU || 0;
  const mau = summaryMetrics.avgMAU || 0;
  const invited = summaryMetrics.totalTenantsInvited || 0;
  const reviewed = summaryMetrics.totalTenantsReviewed || 0;
  const checks = summaryMetrics.totalTenantChecksDone || 0;
  const imported = summaryMetrics.totalPropertiesImported || 0;
  const awarded = summaryMetrics.totalPropertiesAwarded || 0;

  // Derived
  const wauMauRatio = safeDiv(wau, mau).toFixed(2);
  const isSticky = wauMauRatio >= 0.4;
  const reviewedPct = safePercent(reviewed, invited);
  const awardedPct = safePercent(awarded, imported);

  const getBarWidth = (pct) => `calc(${pct}% - ${(48 * pct) / 100}px)`;

  if (loadingSummary) {
    return <ActivityMetricsSkeleton />;
  }

  return (
    <div className="activity-metrics-wrapper mb-4">
      {/* FUNNEL METRICS */}
      <div className="metrics-section">
        <h6 className="metrics-section-title">FUNNEL METRICS</h6>
        <div className="metrics-cards-row-border">
          <div className="activity-metric-card">
            <div className="activity-metric-title">Landlord profiles</div>
            <div className="activity-metric-value">{profiles}</div>
            <div className="activity-metric-subtitle">signed up in period</div>
            <div className="activity-metric-line line-blue-light" style={{ width: 'calc(100% - 48px)' }}></div>
          </div>
          <div className="activity-metric-card">
            <div className="activity-metric-title">Landlords activated</div>
            <div className="activity-metric-value">{activated}</div>
            <div className="activity-metric-subtitle">{activationRate}% of profiles</div>
            <div className="activity-metric-line line-green" style={{ width: getBarWidth(activationRate) }}></div>
          </div>
          <div className="activity-metric-card">
            <div className="activity-metric-title">Avg time to 1st core action</div>
            <div className="activity-metric-value">{formattedTime}</div>
            <div className="activity-metric-subtitle color-green">from profile creation</div>
            <div className="activity-metric-line line-green-dark" style={{ width: 'calc(100% - 48px)' }}></div>
          </div>
        </div>
        <div className="metrics-legend">
          <div className="legend-item">
            <span className="dot dot-blue-light"></span>
            <strong>Profile:</strong> landlord who completes signup and creates an account.
          </div>
          <div className="legend-item">
            <span className="dot dot-green"></span>
            <strong>Activated:</strong> landlord who has completed at least one core action — property imported, tenant invited, tenant reviewed, or tenant check run — at any point after signup. One action is enough.
          </div>
          <div className="legend-item">
            <span className="dot dot-green-dark"></span>
            <strong>Avg time to 1st core action:</strong> average time between profile creation and the first core action completed, across all activated landlords in the period.
          </div>
        </div>
      </div>

      {/* ACTIVITY METRICS */}
      <div className="metrics-section mt-5">
        <h6 className="metrics-section-title">ACTIVITY METRICS</h6>
        <div className="metrics-cards-row wrap-cards">
          <div className="activity-metric-card metric-card-filled">
            <div className="activity-metric-title">Avg WAU (rolling)</div>
            <div className="activity-metric-value">{wau.toFixed(0)}</div>
            <div className="activity-metric-subtitle">landlords / week avg</div>
          </div>
          <div className="activity-metric-card metric-card-filled">
            <div className="activity-metric-title">Avg MAU (rolling)</div>
            <div className="activity-metric-value">{mau.toFixed(0)}</div>
            <div className="activity-metric-subtitle">
              <span className={isSticky ? "color-green font-medium" : ""}>WAU/MAU {wauMauRatio} — {isSticky ? "sticky" : "needs improvement"}</span>
            </div>
          </div>
          <div className="activity-metric-card metric-card-filled">
            <div className="activity-metric-title">Total tenants invited</div>
            <div className="activity-metric-value">{invited}</div>
            <div className="activity-metric-subtitle">this period</div>
          </div>
          <div className="activity-metric-card metric-card-filled">
            <div className="activity-metric-title">Total tenants reviewed</div>
            <div className="activity-metric-value">{reviewed}</div>
            <div className="activity-metric-subtitle color-brown">{reviewedPct}% of invited</div>
          </div>
          <div className="activity-metric-card metric-card-filled">
            <div className="activity-metric-title">Total tenant checks</div>
            <div className="activity-metric-value">{checks}</div>
            <div className="activity-metric-subtitle">this period</div>
          </div>
          <div className="activity-metric-card metric-card-filled">
            <div className="activity-metric-title">Total properties imported</div>
            <div className="activity-metric-value">{imported}</div>
            <div className="activity-metric-subtitle">this period</div>
          </div>
          <div className="activity-metric-card metric-card-filled">
            <div className="activity-metric-title">Total properties awarded</div>
            <div className="activity-metric-value">{awarded}</div>
            <div className="activity-metric-subtitle color-brown">{awardedPct}% of imported</div>
          </div>
        </div>
        
        <div className="metrics-legend">
          <div className="legend-item">
            <span className="dot dot-blue"></span>
            <strong>Active landlord (WAU/MAU):</strong> distinct landlord owner who signed in at least twice on labor days within the period and completed at least one core action — invite tenant, review tenant, run a tenant check, import property, or award a property.
          </div>
          <div className="legend-item">
            <span className="dot dot-blue"></span>
            <strong>Rolling avg:</strong> average across all weeks (WAU) or months (MAU) within the selected date range, not a single snapshot.
          </div>
          <div className="legend-item">
            <span className="dot dot-green"></span>
            <strong>WAU/MAU ratio:</strong> stickiness indicator. Above 0.4 is healthy; above 0.5 indicates the product is part of weekly workflow.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityMetrics;
